import { injectIntl } from "react-intl";
import React, { useEffect, useMemo, useState } from "react";
import apiService from "src/services/api.service";
import {
  PUBLIC_TOP_OFFERS_TABLE_TITLES,
  PUBLIC_TOP_OFFERS_TABLE_TITLE_WIDTHS,
} from "src/helpers/Constants";
import { ListType } from "src/helpers/Enums";
import PublicOfferRow from "./PublicOfferRow";

const formatScore = (score) => {
  if (score == null || isNaN(score)) {
    return "-";
  }
  const numValue = parseFloat(score);
  if (Number.isInteger(numValue)) {
    return numValue.toString();
  }
  return numValue.toFixed(2).replace(/\.00$/, "");
};

const sortByScoreDesc = (list) =>
  [...(list || [])].sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0));

const PublicDashboard = () => {
  const [emailOffers, setEmailOffers] = useState([]);
  const [smsOffers, setSmsOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectedTab, setSelectedTab] = useState(ListType.Email);
  const [openOfferKey, setOpenOfferKey] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    apiService
      .getPublicTopOffers()
      .then((response) => {
        const data = response?.data || {};
        setEmailOffers(Array.isArray(data?.email) ? data.email : []);
        setSmsOffers(Array.isArray(data?.sms) ? data.sms : []);
      })
      .catch(() => {
        setHasError(true);
        setEmailOffers([]);
        setSmsOffers([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const sortedEmailOffers = useMemo(
    () => sortByScoreDesc(emailOffers),
    [emailOffers]
  );
  const sortedSmsOffers = useMemo(
    () => sortByScoreDesc(smsOffers),
    [smsOffers]
  );

  const activeOffers =
    selectedTab === ListType.SMS ? sortedSmsOffers : sortedEmailOffers;

  const handleSelectTab = (tab) => {
    if (tab === selectedTab) return;
    setSelectedTab(tab);
    setOpenOfferKey(null);
  };

  const toggleOffer = (offerKey) => {
    setOpenOfferKey((prev) => (prev === offerKey ? null : offerKey));
  };

  const renderTabButton = (tab, label, iconActive, iconInactive, count) => {
    const isActive = selectedTab === tab;
    return (
      <button
        type="button"
        onClick={() => handleSelectTab(tab)}
        className={isActive ? "OfferDetailsChosenTab" : "OfferDetailsTab"}
      >
        <img
          alt={label}
          src={isActive ? iconActive : iconInactive}
          className="w-3 h-3"
        />
        {label}
        <span
          className={`inline-flex items-center h-[18px] px-[6px] rounded-[34px] text-[11px] font-semibold ${
            isActive
              ? "bg-[#F2EBFB] text-[#7E50EA]"
              : "bg-[#F6F6F9] text-[#8185A8]"
          }`}
          style={{ fontFamily: "Inter" }}
        >
          {count}
        </span>
      </button>
    );
  };

  const renderOffersList = () => {
    if (activeOffers.length === 0) {
      return (
        <div className="flex w-full h-[300px] items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-4">
            <img
              alt="Empty"
              src="/media/icons/rowsPurple.svg"
              className="w-12 h-12"
            />
            <div className="flex flex-col gap-1 justify-center items-center">
              <div className="EmptyCaseTitle">
                No Top {selectedTab} Offers Available
              </div>
              <div className="EmptyCaseDescription">
                Check back later to see the highest scoring {selectedTab}{" "}
                offers.
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="TableHeader">
          {PUBLIC_TOP_OFFERS_TABLE_TITLES.map((title, index) => (
            <div
              key={index}
              className="flex items-center h-full py-2 px-[10px] gap-1"
              style={{ width: PUBLIC_TOP_OFFERS_TABLE_TITLE_WIDTHS[index] }}
            >
              <div className="HeaderNameSection">{title}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          {activeOffers.map((offer, index) => {
            const offerKey = `${selectedTab}-${offer?._id ?? index}`;
            return (
              <PublicOfferRow
                key={offerKey}
                offer={offer}
                rank={index + 1}
                channel={selectedTab}
                isOpen={openOfferKey === offerKey}
                onToggle={() => toggleOffer(offerKey)}
                formatScore={formatScore}
              />
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div
      className="flex flex-col w-full min-h-screen bg-white"
      style={{ fontFamily: "Inter" }}
    >
      <div
        className="flex w-full items-center justify-between px-6 py-5 border-b border-[#E5E7F0] bg-white"
        style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
      >
        <div className="flex items-center gap-3">
          <img
            alt="Logo"
            src="/media/icons/stars-01-purple.svg"
            className="w-6 h-6"
          />
          <div className="flex flex-col">
            <div
              className="text-[#15151F] text-[20px] font-bold leading-[24px]"
              style={{ fontFamily: "Inter" }}
            >
              Top Offers
            </div>
            <div
              className="text-[#8185A8] text-[12px] font-medium leading-[16px]"
              style={{ fontFamily: "Inter" }}
            >
              Highest scoring offers and their creatives
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-full px-6 py-6 gap-4">
        {isLoading ? (
          <div className="flex items-center justify-center w-full h-[400px]">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7F56D9]"></div>
              <div
                className="text-[#15151F] text-sm font-medium"
                style={{ fontFamily: "Inter" }}
              >
                Loading top offers...
              </div>
            </div>
          </div>
        ) : hasError ? (
          <div className="flex w-full h-[400px] items-center justify-center">
            <div className="flex flex-col items-center justify-center gap-4">
              <img
                alt="Error"
                src="/media/icons/warningRed.svg"
                className="w-10 h-10"
              />
              <div className="flex flex-col gap-1 justify-center items-center">
                <div className="EmptyCaseTitle">Unable to load offers</div>
                <div className="EmptyCaseDescription">
                  Please try refreshing the page.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex h-[36px] flex-col items-start gap-[10px] self-stretch border-b border-[#E5E7F0] flex-shrink-0">
              <div className="flex items-center gap-[18px]">
                {renderTabButton(
                  ListType.Email,
                  "Email Top Offers",
                  "/media/icons/mailPurple.svg",
                  "/media/icons/mailPurple.svg",
                  sortedEmailOffers.length
                )}
                <div className="w-[1px] self-stretch rounded bg-[#E5E7F0]"></div>
                {renderTabButton(
                  ListType.SMS,
                  "SMS Top Offers",
                  "/media/icons/chat.svg",
                  "/media/icons/chat.svg",
                  sortedSmsOffers.length
                )}
              </div>
            </div>

            {renderOffersList()}
          </>
        )}
      </div>
    </div>
  );
};

export default injectIntl(PublicDashboard);
